import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../API';
import styles from './UserPicker.module.css';

/**
 * UserPicker — a typeahead component for selecting Girder users.
 *
 * On focus, immediately fetches up to 25 users with no query (scrollable list).
 * Typing refines the list. Clicking outside closes the dropdown.
 *
 * Props:
 *   onSelect     — called with the full user object when a user is chosen
 *   excludeIds   — array of userId strings to omit from results
 *   placeholder  — input placeholder text
 *   autoFocus    — if true, focuses the input on mount
 *   openOnFocus  — if true (default), opens dropdown on focus
 *   showEmail    — if true, include email in dropdown label
 */
function UserPicker({
    onSelect,
    excludeIds = [],
    placeholder = 'Search users…',
    autoFocus = false,
    disabled = false,
    openOnFocus = true,
    showEmail = false,
}) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const debounceRef = useRef(null);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [autoFocus]);

    useEffect(() => {
        function handleClickOutside(e) {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchUsers = useCallback((text) => {
        setLoading(true);
        const req = text
            ? api.get('resource/search', {
                query: {
                    q: text,
                    mode: 'prefixNoSystem',
                    types: JSON.stringify(['user']),
                    limit: 25,
                    offset: 0,
                },
            })
            : api.get('user', { query: { limit: 25 } });

        req.then((r) => {
            if (r.status === 200) {
                const raw = text ? (r.data?.user || []) : (r.data || []);
                const filtered = (raw || []).filter(
                    (u) =>
                        !excludeIds.includes(String(u._id)) &&
                        !String(u.login).startsWith('system-user')
                );
                setResults(filtered);
                setOpen(true);
            }
        })
            .catch(() => setResults([]))
            .finally(() => setLoading(false));
    }, [excludeIds]); // eslint-disable-line react-hooks/exhaustive-deps

    function handleFocus() {
        if (!openOnFocus) return;
        // Show the full list immediately when the user activates the picker
        if (!open) {
            fetchUsers(query.trim() || undefined);
        }
    }

    function handleKeyDown(e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            setOpen(false);
        }
    }

    function handleBlur(e) {
        const next = e.relatedTarget;
        if (wrapperRef.current && next && wrapperRef.current.contains(next)) {
            return;
        }
        setOpen(false);
    }

    function handleChange(e) {
        if (disabled) return;
        const val = e.target.value;
        setQuery(val);

        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            fetchUsers(val.trim() || undefined);
        }, 200);
    }

    function handleSelect(user) {
        onSelect(user);
        setQuery('');
        setResults([]);
        setOpen(false);
    }

    function formatUser(user) {
        const full = [user.firstName, user.lastName].filter(Boolean).join(' ');
        const base = full ? `${user.login} (${full})` : user.login;
        const email = (user.email || '').trim();
        if (showEmail && email) {
            return `${base} — ${email}`;
        }
        return base;
    }

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            <input
                ref={inputRef}
                type="text"
                className={styles.input}
                value={query}
                onChange={handleChange}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                placeholder={placeholder}
                autoComplete="off"
                disabled={disabled}
            />
            {loading && <span className={styles.loadingHint}>Loading…</span>}
            {open && results.length > 0 && (
                <ul className={styles.dropdown}>
                    {results.map((user) => (
                        <li key={user._id}>
                            <button
                                type="button"
                                className={styles.dropdownItem}
                                // Use mousedown so the click registers before onBlur closes the dropdown
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleSelect(user);
                                }}
                            >
                                {formatUser(user)}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {open && !loading && results.length === 0 && (
                <ul className={styles.dropdown}>
                    <li className={styles.noResults}>No matching users.</li>
                </ul>
            )}
        </div>
    );
}

export default UserPicker;
